import { MobType } from './types'
import type { MobData } from './types'
import type { SphericalSpatialHash } from './spatialHash'

// ─── Flocking parameters ────────────────────────────────────────────
interface FlockParams {
  neighborRadius: number     // angular radians
  separationDist: number     // angular radians
  separationWeight: number
  alignmentWeight: number
  cohesionWeight: number
}

const SHEEP_FLOCK: FlockParams = {
  neighborRadius: 0.008,     // ~80 units
  separationDist: 0.002,     // ~20 units
  separationWeight: 0.06,
  alignmentWeight: 0.03,
  cohesionWeight: 0.01,
}

const CHICKEN_FLOCK: FlockParams = {
  neighborRadius: 0.004,     // ~40 units
  separationDist: 0.001,     // ~10 units
  separationWeight: 0.08,
  alignmentWeight: 0.01,     // chickens barely align
  cohesionWeight: 0.005,
}

const HORSE_FLOCK: FlockParams = {
  neighborRadius: 0.010,     // ~100 units
  separationDist: 0.003,     // ~30 units
  separationWeight: 0.04,
  alignmentWeight: 0.05,     // horses herd well
  cohesionWeight: 0.02,
}

const WOLF_FLOCK: FlockParams = {
  neighborRadius: 0.008,     // ~80 units
  separationDist: 0.002,     // ~20 units
  separationWeight: 0.05,
  alignmentWeight: 0.06,     // pack alignment
  cohesionWeight: 0.03,
}

// ─── Tangent frame (inline for perf) ────────────────────────────────
function tangentFrame(nx: number, ny: number, nz: number) {
  let ex: number, ey: number, ez: number
  if (Math.abs(ny) < 0.9) {
    ex = -nz; ey = 0; ez = nx
  } else {
    ex = 0; ey = nz; ez = -ny
  }
  const el = Math.sqrt(ex * ex + ey * ey + ez * ez)
  ex /= el; ey /= el; ez /= el
  const fx = ny * ez - nz * ey
  const fy = nz * ex - nx * ez
  const fz = nx * ey - ny * ex
  return { ex, ey, ez, fx, fy, fz }
}

// ─── Apply flocking forces to a mob's wanderAngle ───────────────────
export function applyFlocking(
  mob: MobData,
  allMobs: MobData[],
  spatialHash: SphericalSpatialHash,
) {
  const FLOCK_PARAMS: Record<number, FlockParams> = {
    [MobType.Sheep]: SHEEP_FLOCK,
    [MobType.Chicken]: CHICKEN_FLOCK,
    [MobType.Horse]: HORSE_FLOCK,
    [MobType.Wolf]: WOLF_FLOCK,
  }
  const params = FLOCK_PARAMS[mob.type] ?? SHEEP_FLOCK

  const neighborIndices = spatialHash.getNeighborIndices(mob.nx, mob.ny, mob.nz)
  if (neighborIndices.length <= 1) return  // alone, no flocking

  const { ex, ey, ez, fx, fy, fz } = tangentFrame(mob.nx, mob.ny, mob.nz)

  let sepE = 0, sepF = 0
  let aliE = 0, aliF = 0
  let cohE = 0, cohF = 0
  let count = 0

  for (let k = 0; k < neighborIndices.length; k++) {
    const other = allMobs[neighborIndices[k]]
    if (other === mob) continue
    if (other.type !== mob.type) continue  // only flock with same species

    // Angular distance
    const dot = mob.nx * other.nx + mob.ny * other.ny + mob.nz * other.nz
    const angDist = Math.acos(Math.min(1, Math.max(-1, dot)))
    if (angDist > params.neighborRadius) continue

    // Project neighbor into tangent plane
    const dx = other.nx - mob.nx
    const dy = other.ny - mob.ny
    const dz = other.nz - mob.nz
    const projE = dx * ex + dy * ey + dz * ez
    const projF = dx * fx + dy * fy + dz * fz

    count++

    // Separation: push away from close neighbors
    if (angDist < params.separationDist && angDist > 0.0001) {
      const invDist = 1 / angDist
      sepE -= projE * invDist
      sepF -= projF * invDist
    }

    // Alignment: match heading direction
    const otherCos = Math.cos(other.heading)
    const otherSin = Math.sin(other.heading)
    aliE += otherSin
    aliF += otherCos

    // Cohesion: steer toward center of neighbors
    cohE += projE
    cohF += projF
  }

  if (count === 0) return

  // Normalize
  aliE /= count; aliF /= count
  cohE /= count; cohF /= count

  // Combine forces
  const forceE = sepE * params.separationWeight + aliE * params.alignmentWeight + cohE * params.cohesionWeight
  const forceF = sepF * params.separationWeight + aliF * params.alignmentWeight + cohF * params.cohesionWeight

  // Apply as steering (blend into wanderAngle)
  const forceMag = Math.sqrt(forceE * forceE + forceF * forceF)
  if (forceMag > 0.0001) {
    const forceAngle = Math.atan2(forceE, forceF)
    // Gentle blend
    let diff = forceAngle - mob.wanderAngle
    while (diff > Math.PI) diff -= Math.PI * 2
    while (diff < -Math.PI) diff += Math.PI * 2
    mob.wanderAngle += diff * Math.min(forceMag * 10, 0.3)
  }
}
